import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Calendar, MessageSquare, ArrowRight, ShoppingBag, FileText, Video } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../i18n/LanguageContext';
interface OrderConfirmationPageProps {
  onNavigate: (page: string) => void;
}
export function OrderConfirmationPage({
  onNavigate
}: OrderConfirmationPageProps) {
  const {
    mainItem,
    totalPrice
  } = useCart();
  const {
    t
  } = useLanguage();
  // Generate stable order number for this session
  const orderNumber = useMemo(() => 'ORD-' + new Date().getFullYear() + '-' + Math.floor(1000 + Math.random() * 9000), []);
  const orderDate = new Date().toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  // Use full cart total, fallback to mainItem price, then default
  const price = totalPrice > 0 ? totalPrice : mainItem ? mainItem.price : 5000;
  const formatPrice = (p: number) => p.toLocaleString('ru-RU') + ' ₽';
  return <div className="w-full bg-slate-50 min-h-screen pb-24 pt-12" data-id="element-21333">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21334">
        {/* Success Header */}
        <div className="text-center mb-12" data-id="element-21335">
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-21336">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" data-id="element-21337" />
          </motion.div>

          <motion.h1 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-21338">
            {t('order.success.title')}
          </motion.h1>

          <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-lg text-slate-600 max-w-xl mx-auto" data-id="element-21339">
            {t('order.success.desc')}
          </motion.p>
        </div>

        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="space-y-6" data-id="element-21340">
          {/* Order Details Card */}
          <Card padding="lg" className="border-emerald-100 shadow-lg relative overflow-hidden" data-id="element-21341">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-full -z-10" data-id="element-21342"></div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-slate-100" data-id="element-21343">
              <div data-id="element-21344">
                <div className="text-sm text-slate-500 mb-1" data-id="element-21345">
                  {t('order.details.number')}
                </div>
                <div className="text-lg font-bold text-slate-900" data-id="element-21346">
                  {orderNumber}
                </div>
              </div>
              <div className="sm:text-right" data-id="element-21347">
                <div className="text-sm text-slate-500 mb-1" data-id="element-21348">
                  {t('order.details.date')}
                </div>
                <div className="text-lg font-medium text-slate-900" data-id="element-21349">
                  {orderDate}
                </div>
              </div>
            </div>

            <div className="space-y-6" data-id="element-21350">
              <h3 className="font-bold text-slate-900" data-id="element-21351">
                {t('order.details.title')}
              </h3>

              <div className="flex items-start gap-4" data-id="element-21352">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0" data-id="element-21353">
                  {mainItem?.type === 'package' ? <ShoppingBag className="w-6 h-6 text-indigo-600" data-id="element-21354" /> : mainItem?.type === 'premium-report' ? <FileText className="w-6 h-6 text-indigo-600" data-id="element-21355" /> : <Video className="w-6 h-6 text-indigo-600" data-id="element-21356" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-21357">
                  <div className="flex items-center gap-2 mb-1" data-id="element-21358">
                    {mainItem?.badges?.map((badge, i) => <Badge key={i} variant={badge.variant as any} className="text-[10px]" data-id="element-21359">
                        {badge.label}
                      </Badge>) || <Badge variant="pro" className="bg-indigo-100 text-indigo-700 border-none text-[10px]" data-id="element-21360">
                        Консультация
                      </Badge>}
                  </div>
                  <h4 className="font-bold text-slate-900 text-base mb-1" data-id="element-21361">
                    {mainItem?.name || 'Полный разбор натальной карты'}
                  </h4>
                  {mainItem?.astrologer && <div className="text-sm text-slate-500" data-id="element-21362">
                      {t('order.details.astrologer', {
                    name: mainItem.astrologer.name
                  })}
                    </div>}
                </div>
                <div className="font-bold text-slate-900" data-id="element-21363">
                  {formatPrice(price)}
                </div>
              </div>

              <div className="pt-6 border-t border-slate-100 flex justify-between items-center" data-id="element-21364">
                <span className="text-lg font-bold text-slate-900" data-id="element-21365">
                  {t('order.details.total')}
                </span>
                <span className="text-2xl font-bold text-emerald-600" data-id="element-21366">
                  {formatPrice(price)}
                </span>
              </div>
            </div>
          </Card>

          {/* Next Steps */}
          <Card padding="lg" className="bg-indigo-50/50 border-indigo-100" data-id="element-21367">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-21368">
              <Calendar className="w-5 h-5 text-indigo-600" data-id="element-21369" />
              {t('order.next.title')}
            </h3>

            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-indigo-200 before:to-transparent" data-id="element-21370">
              {mainItem?.type === 'package' ? <>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-21371">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-600 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-21372">
                      1
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-indigo-100 bg-white shadow-sm" data-id="element-21373">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-21374">
                        {t('order.next.pkg.1.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-21375">
                        {t('order.next.pkg.1.desc')}
                      </div>
                    </div>
                  </div>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-21376">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-200 text-indigo-700 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-21377">
                      2
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-white shadow-sm" data-id="element-21378">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-21379">
                        {t('order.next.pkg.2.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-21380">
                        {t('order.next.pkg.2.desc')}
                      </div>
                    </div>
                  </div>
                </> : mainItem?.type === 'premium-report' ? <>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-21381">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-600 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-21382">
                      1
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-indigo-100 bg-white shadow-sm" data-id="element-21383">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-21384">
                        {t('order.next.report.1.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-21385">
                        {t('order.next.report.1.desc')}
                      </div>
                    </div>
                  </div>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-21386">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-200 text-indigo-700 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-21387">
                      2
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-white shadow-sm" data-id="element-21388">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-21389">
                        {t('order.next.report.2.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-21390">
                        {t('order.next.report.2.desc')}
                      </div>
                    </div>
                  </div>
                </> : <>
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-21391">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-600 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-21392">
                      1
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-indigo-100 bg-white shadow-sm" data-id="element-21393">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-21394">
                        {t('order.next.consult.1.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-21395">
                        {t('order.next.consult.1.desc', {
                      name: mainItem?.astrologer?.name || 'Елена Смирнова'
                    })}
                      </div>
                    </div>
                  </div>

                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-21396">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-indigo-200 text-indigo-700 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-21397">
                      2
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-white shadow-sm" data-id="element-21398">
                      <div className="font-bold text-slate-900 mb-1 text-sm" data-id="element-21399">
                        {t('order.next.consult.2.title')}
                      </div>
                      <div className="text-slate-600 text-xs leading-relaxed" data-id="element-21400">
                        {t('order.next.consult.2.desc')}
                      </div>
                    </div>
                  </div>
                </>}
            </div>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-21401">
            <Button size="lg" className="flex-1" onClick={() => onNavigate('dashboard-user')} icon={<ArrowRight className="w-4 h-4" data-id="element-21403" />} data-id="element-21402">
              {t('order.btn.dashboard')}
            </Button>
            <Button variant="outline" size="lg" className="flex-1" onClick={() => onNavigate('marketplace')} icon={<ShoppingBag className="w-4 h-4" data-id="element-21405" />} data-id="element-21404">
              {t('order.btn.catalog')}
            </Button>
          </div>
        </motion.div>
      </div>
    </div>;
}